import React from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Home,
  Sun,
  Eye,
  Shield,
  Maximize
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ShutterStylesPage = () => {
  const shutterStyles = [
    {
      name: 'Full Height Shutters',
      subtitle: 'Complete Coverage',
      description: 'The most popular choice offering complete window coverage with optional mid-rail for independent top and bottom light control. Perfect for most window types.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/s9oh1m7t_03.%20White%20Shutters.jpg',
      features: [
        'Complete window coverage',
        'Optional mid-rail for flexibility',
        'Maximum privacy control',
        'Elegant, uninterrupted lines',
        'Perfect for standard windows',
        'Easy to operate'
      ],
      benefits: [
        'Maximum Privacy',
        'Complete Light Control',
        'Enhanced Insulation',
        'Timeless Elegance'
      ],
      idealFor: ['Standard windows', 'Floor-to-ceiling windows', 'French doors'],
      icon: Shield,
      color: 'text-[#8CB89F]'
    },
    {
      name: 'Tier on Tier',
      subtitle: 'Ultimate Flexibility',
      description: 'Two separate panel sets (top and bottom) that operate independently, offering the ultimate in light and privacy control. Perfect for busy streets or tall windows.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/it53cftc_08.%20London%20Smoke%20Shutters%20-%20Tier%20on%20Tier%20Open.jpg',
      features: [
        'Two independent panel sets',
        'Ultimate flexibility',
        'Top and bottom separate control',
        'Perfect for tall windows',
        'Street-facing window ideal',
        'Versatile light management'
      ],
      benefits: [
        'Independent Control',
        'Privacy + Light',
        'Street Visibility',
        'Professional Appearance'
      ],
      idealFor: ['Tall windows', 'Street-facing rooms', 'Living areas'],
      icon: Maximize,
      color: 'text-green-600'
    },
    {
      name: 'Café Style',
      subtitle: 'Light & Privacy',
      description: 'Covers only the lower portion of the window, maintaining privacy at eye level while maximizing natural light from above. Perfect for ground floor rooms.',
      image: 'https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/25wu22aa_cafe%20style.jpeg',
      features: [
        'Lower window coverage only',
        'Maximum natural light',
        'Street-level privacy',
        'Charming traditional style',
        'Cost-effective solution',
        'Easy maintenance'
      ],
      benefits: [
        'Enhanced Privacy',
        'Maximum Light',
        'Traditional Charm',
        'Cost Effective'
      ],
      idealFor: ['Ground floor windows', 'Kitchen windows', 'Street-facing rooms'],
      icon: Sun,
      color: 'text-yellow-600'
    }
  ];

  const styleComparison = [
    { feature: 'Light Control', fullHeight: '★★★★★', tierOnTier: '★★★★★', cafe: '★★★☆☆' },
    { feature: 'Privacy', fullHeight: '★★★★★', tierOnTier: '★★★★★', cafe: '★★★☆☆' },
    { feature: 'Flexibility', fullHeight: '★★★★☆', tierOnTier: '★★★★★', cafe: '★★☆☆☆' },
    { feature: 'Cost', fullHeight: '★★★☆☆', tierOnTier: '★★☆☆☆', cafe: '★★★★★' },
    { feature: 'Maintenance', fullHeight: '★★★★☆', tierOnTier: '★★★☆☆', cafe: '★★★★★' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-50 to-[#8CB89F]/10 py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Discover Your Perfect
              <span className="block text-[#8CB89F]">Shutter Style</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              From classic full-height to flexible tier-on-tier, find the perfect style 
              to complement your home's architecture and meet your specific needs.
            </p>
          </div>
        </div>
      </section>

      {/* Styles Detail Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="space-y-20">
            {shutterStyles.map((style, index) => (
              <div key={index} className={`${index % 2 === 1 ? 'lg:flex-row-reverse' : ''} lg:flex items-center gap-12`}>
                {/* Image */}
                <div className="lg:w-1/2 mb-8 lg:mb-0">
                  <div className="relative">
                    <img 
                      src={style.image} 
                      alt={style.name}
                      className="w-full h-96 object-cover rounded-2xl shadow-xl"
                    />
                    <div className="absolute top-4 left-4">
                      <Badge className={`${style.color} bg-white/90`}>
                        <style.icon className="h-4 w-4 mr-1" />
                        {style.subtitle}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="lg:w-1/2">
                  <h2 className="text-4xl font-bold text-gray-900 mb-4">{style.name}</h2>
                  <p className="text-lg text-gray-600 mb-6 leading-relaxed">{style.description}</p>
                  
                  {/* Features */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {style.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Benefits:</h4>
                    <div className="flex flex-wrap gap-2">
                      {style.benefits.map((benefit, i) => (
                        <Badge key={i} variant="outline" className={style.color}>{benefit}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Ideal For */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Ideal for:</h4>
                    <div className="flex flex-wrap gap-2">
                      {style.idealFor.map((room, i) => (
                        <Badge key={i} variant="secondary">{room}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Style Comparison</h2>
            <p className="text-xl text-gray-600">
              Compare key features to find the perfect style for your needs
            </p>
          </div>

          <Card className="overflow-hidden shadow-xl">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold text-[#8CB89F]">Full Height</th>
                      <th className="text-center p-4 font-semibold text-green-600">Tier on Tier</th>
                      <th className="text-center p-4 font-semibold text-yellow-600">Café Style</th>
                    </tr>
                  </thead>
                  <tbody>
                    {styleComparison.map((row, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="p-4 font-medium">{row.feature}</td>
                        <td className="p-4 text-center">{row.fullHeight}</td>
                        <td className="p-4 text-center">{row.tierOnTier}</td>
                        <td className="p-4 text-center">{row.cafe}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Found Your Perfect Shutter Style?</h2>
          <p className="text-xl mb-8 opacity-90">
            Get instant pricing for your chosen shutter style with Aston Lily Joinery's pricing calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Get Instant Pricing
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white px-8 py-4 text-lg"
                style={{'--hover-color': '#8CB89F'}}
              >
                <Home className="mr-2 h-5 w-5" />
                Book Consultation
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ShutterStylesPage;